/**
 * PayPalButton — carga el SDK de PayPal y muestra los botones de pago
 * Incluye: PayPal, Google Pay, Apple Pay, tarjeta (según dispositivo)
 *
 * Props:
 *   onSuccess(detalles) — llamado cuando el pago se confirma en el backend
 *   onError(msg)        — llamado si algo falla
 *   disabled            — deshabilita el botón
 */
import React, { useEffect, useRef, useState } from 'react';

const PAYPAL_CLIENT_ID = 'AWdRZ6JH9tAtP4RwHTABV8T_Wnshev6EAhjuY5t2BI7t-q3lyH_uqwKIHpSkpiG842pONfCrV59nSmR-';
const PRECIO           = '1.99';

function PayPalButton({ onSuccess, onError, disabled }) {
  const containerRef = useRef(null);
  const buttonsRef   = useRef(null);
  const [sdkReady, setSdkReady]     = useState(false);
  const [cargando, setCargando]     = useState(true);
  const [errorSdk, setErrorSdk]     = useState('');

  // ── Cargar el SDK de PayPal dinámicamente ──────────────
  useEffect(() => {
    if (window.paypal) { setSdkReady(true); setCargando(false); return; }

    const script    = document.createElement('script');
    script.src      = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&intent=capture&components=buttons&disable-funding=card,credit`;
    script.async    = true;
    script.onload   = () => { setSdkReady(true); setCargando(false); };
    script.onerror  = () => {
      setCargando(false);
      setErrorSdk('No se pudo cargar PayPal. Verifica tu conexión.');
    };
    document.body.appendChild(script);

    return () => {
      // Limpiar al desmontar
      if (buttonsRef.current) { try { buttonsRef.current.close(); } catch(e) {} }
    };
  }, []);

  // ── Renderizar botones cuando el SDK está listo ────────
  useEffect(() => {
    if (!sdkReady || !containerRef.current || disabled) return;
    if (buttonsRef.current) { try { buttonsRef.current.close(); } catch(e) {} }

    buttonsRef.current = window.paypal.Buttons({
      style: {
        layout:  'vertical',
        color:   'gold',
        shape:   'pill',
        label:   'pay',
        height:  48,
      },

      // 1️⃣ Crear orden en nuestro backend
      createOrder: async () => {
        try {
          const res  = await fetch('/api/paypal/create-order', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          const data = await res.json();
          if (!res.ok || !data.id) throw new Error(data.error || 'Error al crear orden');
          return data.id;
        } catch (err) {
          onError?.(err.message);
          throw err;
        }
      },

      // 2️⃣ Capturar y confirmar en nuestro backend
      onApprove: async (data) => {
        try {
          const res    = await fetch('/api/paypal/capture-order', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ orderID: data.orderID }),
          });
          const result = await res.json();
          if (!res.ok || !result.exito) throw new Error(result.error || 'Error al confirmar pago');
          onSuccess?.(result);
        } catch (err) {
          onError?.(err.message);
        }
      },

      onError: (err) => {
        console.error('PayPal error:', err);
        onError?.('Ocurrió un error con PayPal. Intenta de nuevo.');
      },

      onCancel: () => {
        // El usuario cerró el popup — no hacemos nada
      },
    });

    if (buttonsRef.current.isEligible()) {
      buttonsRef.current.render(containerRef.current);
    }
  }, [sdkReady, disabled, onSuccess, onError]);

  if (errorSdk) {
    return (
      <p style={{ color: 'var(--color-acento)', fontSize: '0.82rem', textAlign: 'center' }}>
        {errorSdk}
      </p>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: '50px', position: 'relative' }}>
      {cargando && (
        <div style={{ textAlign: 'center', color: 'var(--color-texto-suave)', fontSize: '0.82rem', padding: '0.8rem' }}>
          Cargando opciones de pago…
        </div>
      )}
      <div
        ref={containerRef}
        style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
      />
    </div>
  );
}

export default PayPalButton;
